import { CreditCard, PaymentMethodTypes } from '@accedo/one-plugin-sdk';
import { useCallback, useMemo, useState } from 'react';

import { Errors } from '#/errors/errors';
import { OneError } from '#/errors/oneError';
import { usePaywallStore } from '#/store/paywall/paywall.store';

import { Actions, PurchaseFlowStore } from './PlanPurchaseFlow.types';

export interface ChangeProductAddPaymentMethodProps {
  store: PurchaseFlowStore;
  actions: Actions;
  onPaymentMethodAdded: () => void;
}

export const useChangeProductAddPaymentMethod = ({
  store,
  actions: { handleError },
  onPaymentMethodAdded,
}: ChangeProductAddPaymentMethodProps) => {
  const { paywallApi } = usePaywallStore();
  const [isFailed, setIsFailed] = useState(false);

  const hasOwnUi = paywallApi.hasOwnUi();

  // the `addPaymentDetails` method in the plugins don't currently support PayPal,
  // at all, so we have to filter it out for the change product flow for now.
  const paymentMethods = useMemo(
    () =>
      (store.selectedProduct?.paymentMethods ?? []).filter(
        ({ type }) => type !== PaymentMethodTypes.Paypal
      ),
    [store.selectedProduct?.paymentMethods]
  );

  const handlePaymentDetailsSuccess = useCallback(() => {
    onPaymentMethodAdded();
  }, [onPaymentMethodAdded]);

  const handlePaymentDetailsFailure = useCallback(
    (err: any) => {
      setIsFailed(true);
      handleError(
        new OneError(Errors.PURCHASE_VALIDATION_FAILED, {
          cause: err,
          extras: {
            method: 'ChangeProductAddPaymentMethod.handlePaymentDetailsFailure',
          },
        })
      );
    },
    [handleError]
  );

  const handleAddPaymentDetails = useCallback(
    async ({ card }: { card?: CreditCard }) => {
      if (!paywallApi.addPaymentDetails) {
        setIsFailed(true);
        handleError(
          new OneError(Errors.WEB_INTEGRATION_GENERAL_OPERATION_NOT_SUPPORTED, {
            extras: { method: 'addPaymentDetails' },
          })
        );

        return {};
      }

      try {
        await paywallApi.addPaymentDetails(card);
      } catch (err) {
        handlePaymentDetailsFailure(err);
      }

      return {};
    },
    [handleError, handlePaymentDetailsFailure, paywallApi]
  );

  // we shouldn't end up here if we already have one, but just in case
  if (store.hasPaymentMethod) {
    onPaymentMethodAdded();
  }

  return {
    shouldRender: !store.hasPaymentMethod && !isFailed,
    hasOwnUi,
    paymentMethods,
    handlePaymentDetailsSuccess,
    handlePaymentDetailsFailure,
    handleAddPaymentDetails,
  };
};
